import { useContext, useState, useEffect, useRef } from "react";
import { AuthContext } from "../../services/context";
import { getCurrentWorkspace } from "../../services/userConfig";
import {
  useGetAllRegistrations,
  useGetRegistration,
  useUpdateRegistration,
} from "../../api/registrations";
import { DataTable } from "../DataTable";
import RegistrationStatusBadge from "./RegistrationStatusBadge";
import dayjs from "dayjs";
import {
  Box,
  Text,
  Flex,
  Group,
  ActionIcon,
  Select,
  TextInput,
  Modal,
} from "@mantine/core";
import {
  IconCheck,
  IconChevronRight,
  IconDotsVertical,
  IconMailFast,
  IconSearch,
  IconX,
  IconXboxX,
  IconEdit,
} from "@tabler/icons-react";
import { TableActions, TableAction } from "../TableActions";
import clsx from "clsx";
import classes from "./registrations.module.css";
import { useConfirm } from "../../hooks/useConfirm";
import TicketCard from "./TicketCard";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { useSearchParams } from "react-router-dom";
import AdditionalFieldDisplay from "./AdditionalFieldDisplay";
import EditRegistrationForm from "./EditRegistrationForm";
import EditTicketsForm from "./EditTicketForm";

function Table({
  selectedRegistrationId,
  onRegistrationSelect,
  isHardBlocked,
}) {
  const { user } = useContext(AuthContext);
  const { eventId } = getCurrentWorkspace(user);

  const [searchParams, setSearchParams] = useSearchParams();

  const [editModalOpened, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);

  const [pagination, setPagination] = useState(() => {
    return {
      page: parseInt(searchParams.get("page") || "1", 10),
      limit: parseInt(searchParams.get("limit") || "10", 10),
    };
  });

  const [sorting, setSorting] = useState(() => {
    try {
      const sortParams = searchParams.get("sort");
      return sortParams
        ? JSON.parse(sortParams)
        : {
            columnAccessor: "createdAt",
            direction: "desc",
          };
    } catch (e) {
      return {
        columnAccessor: "createdAt",
        direction: "desc",
      };
    }
  });

  const [filter, setFilter] = useState(() => {
    try {
      const filterParams = searchParams.get("filter");
      return filterParams ? JSON.parse(filterParams) : null;
    } catch (e) {
      return null;
    }
  });

  const [debouncedFilter] = useDebouncedValue(filter, 200);

  const { data, isPending } = useGetAllRegistrations({
    eventId,
    ...pagination,
    sort: JSON.stringify(sorting),
    filter: debouncedFilter,
  });

  const { mutateAsync } = useUpdateRegistration();
  const confirm = useConfirm();

  const [expandedRow, setExpandedRow] = useState([]);
  const highlightedRowRef = useRef(null);

  // Auto-expand and scroll to registration when selected from history
  useEffect(() => {
    if (selectedRegistrationId) {
      setFilter({ id: selectedRegistrationId.toString() });
      setPagination({ ...pagination, page: 1 });
      setSearchParams((params) => {
        params.set("page", "1");
        params.set(
          "filter",
          JSON.stringify({ id: selectedRegistrationId.toString() })
        );
        return params;
      });
    }
  }, [selectedRegistrationId]);

  // Expand row and scroll when data is loaded
  useEffect(() => {
    if (selectedRegistrationId && data?.data && !isPending) {
      const registration = data.data.find(
        (reg) => reg.id === selectedRegistrationId
      );

      if (registration) {
        setExpandedRow((rows) =>
          rows.includes(selectedRegistrationId)
            ? rows
            : [...rows, selectedRegistrationId]
        );

        const rowElement = document.querySelector(
          `[data-row-id="${selectedRegistrationId}"]`
        );

        if (rowElement) {
          rowElement.scrollIntoView({ behavior: "smooth", block: "center" });
          highlightedRowRef.current = rowElement;
        }

        onRegistrationSelect?.();
      }
    }
  }, [selectedRegistrationId, data, isPending, onRegistrationSelect]);

  const handlePageChange = (page) => {
    setPagination({ ...pagination, page });
    setSearchParams((searchParams) => {
      searchParams.set("page", page.toString());
      return searchParams;
    });
  };

  const handleSortChange = (sort) => {
    setSorting(sort);
    setSearchParams((searchParams) => {
      if (!sort) {
        searchParams.delete("sort");
        return searchParams;
      }
      searchParams.set("sort", JSON.stringify(sort));
      return searchParams;
    });
  };

  const handleFilterChange = (filter) => {
    setFilter(filter);
    setPagination((pagination) => ({ ...pagination, page: 1 }));
    setSearchParams((searchParams) => {
      searchParams.set("page", "1");
      if (!filter) {
        searchParams.delete("filter");
        return searchParams;
      }
      searchParams.set("filter", JSON.stringify(filter));
      return searchParams;
    });
  };

  const handleOpenEditModal = (registrationId) => {
    setSelectedRegistration(registrationId);
    openEditModal();
  };

  const handleCloseEditModal = () => {
    setSelectedRegistration(null);
    closeEditModal();
  };

  return (
    <>
      <DataTable
        records={data?.data}
        pagination={pagination}
        onPageChange={handlePageChange}
        page={pagination.page}
        recordsPerPage={pagination.limit}
        totalRecords={data?.pagination.total}
        onSortStatusChange={handleSortChange}
        sortStatus={sorting}
        borderRadius="md"
        verticalSpacing="md"
        rowExpansion={{
          allowMultiple: true,
          expanded: {
            recordIds: expandedRow,
          },
          content: ({ record }) => (
            <RegistrationTicketsTable registrationId={record.id} />
          ),
        }}
        rowStyle={(record) => ({
          backgroundColor:
            record.id === selectedRegistrationId ? "#e7f5ff" : undefined,
          transition: "background-color 2s ease-out",
        })}
        columns={[
          {
            accessor: "id",
            sortable: true,
            title: "ID",
            render: ({ id }) => (
              <Text size="sm" fw={500} data-row-id={id}>
                #{id}
              </Text>
            ),
            filter: (
              <TextInput
                label="ID"
                description="Filter registrations by ID"
                placeholder="Search by ID"
                value={filter?.id || ""}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  if (value === "") {
                    handleFilterChange(null);
                    return;
                  }
                  handleFilterChange({ ...filter, id: value });
                }}
              />
            ),
            filtering: filter?.id,
          },
          {
            accessor: "type",
            sortable: true,
            title: "Registration type",
            render: ({ type, id }) => (
              <Box component="span" ml={8}>
                <ActionIcon
                  variant="light"
                  color="gray"
                  onClick={() => {
                    setExpandedRow((rows) =>
                      rows.includes(id)
                        ? rows.filter((row) => row !== id)
                        : [...rows, id]
                    );
                  }}
                  mr="sm"
                >
                  <IconChevronRight
                    className={clsx(classes.icon, classes.expandIcon, {
                      [classes.expandIconRotated]: expandedRow.includes(id),
                    })}
                  />
                </ActionIcon>
                <span>{type}</span>
              </Box>
            ),
            filter: (
              <Select
                label="Type"
                description="Show registrations with type"
                placeholder="Filter by type"
                data={[
                  { value: "individual", label: "Individual" },
                  { value: "company", label: "Company" },
                ]}
                value={filter?.type}
                onChange={(value) => {
                  handleFilterChange({ ...filter, type: value });
                }}
                onClear={() => handleFilterChange(null)}
                clearable
              />
            ),
            filtering: filter?.type,
          },
          {
            accessor: "name",
            sortable: true,
            render: ({ companyName, firstName }) => (
              <Box component="span" ml={8}>
                {companyName || firstName}
              </Box>
            ),
            filter: (
              <FilterSearch
                query={filter}
                setQuery={handleFilterChange}
                columnName="name"
              />
            ),
            filtering: filter?.name,
          },
          {
            accessor: "ticketCount",
            sortable: true,
            width: "0%",
            title: "No. of Tickets",
          },
          {
            accessor: "email",
            sortable: true,
            filter: (
              <FilterSearch
                query={filter}
                setQuery={handleFilterChange}
                columnName="email"
              />
            ),
            filtering: filter?.email,
          },
          {
            accessor: "status",
            sortable: true,
            render: ({ status }) => <RegistrationStatusBadge status={status} />,
            filter: (
              <StatusFilter query={filter} setQuery={handleFilterChange} />
            ),
            filtering: filter?.status,
          },
          {
            accessor: "createdAt",
            title: "Created At",
            sortable: true,
            render: ({ createdAt }) =>
              dayjs(createdAt).locale("en").format("DD MMM YYYY - HH:mm"),
          },
          {
            accessor: "actions",
            title: <Box mr={6}>Actions</Box>,
            textAlign: "right",
            render: (record) => {
              return (
                <TableActions>
                  {(record.status === "pending" ||
                    record.status === "invoiced") && (
                    <>
                      {record.status === "pending" && (
                        <TableAction
                          label="Mark as invoiced"
                          icon={IconMailFast}
                          color="orange"
                          onClick={(e) => {
                            e.stopPropagation();
                            mutateAsync({
                              id: record.id,
                              status: "invoiced",
                            });
                          }}
                        />
                      )}
                      <TableAction
                        label={
                          isHardBlocked
                            ? "Approve registration is blocked"
                            : "Approve registration"
                        }
                        icon={IconCheck}
                        color="green"
                        disabled={isHardBlocked}
                        onClick={(e) => {
                          e.stopPropagation();
                          confirm({
                            title: `Approve registration for ${record?.companyName || record?.firstName}?`,
                            message:
                              "Approving the registration will generate and issue tickets for attendees, allowing them to access the event. This action is irreversible. Would you like to proceed?",
                            onConfirm: () =>
                              mutateAsync({
                                id: record.id,
                                status: "approved",
                              }),
                          });
                        }}
                      />
                      <TableAction
                        label="Reject registration"
                        icon={IconXboxX}
                        color="red"
                        onClick={(e) => {
                          e.stopPropagation();
                          confirm({
                            title: "Reject registration?",
                            message:
                              "Rejecting the registration will prevent attendees from accessing the event, and the status cannot be changed afterward. Would you like to proceed with the rejection?",
                            onConfirm: () =>
                              mutateAsync({
                                id: record.id,
                                status: "rejected",
                              }),
                          });
                        }}
                      />
                      <TableAction
                        label="Edit registration"
                        icon={IconEdit}
                        color="blue"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditModal(record.id);
                        }}
                      />
                    </>
                  )}
                  <TableAction
                    label="More information"
                    icon={IconDotsVertical}
                    color="blue"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedRow((rows) =>
                        rows.includes(record.id)
                          ? rows.filter((row) => row !== record.id)
                          : [...rows, record.id]
                      );
                    }}
                  />
                </TableActions>
              );
            },
          },
        ]}
        fetching={isPending}
      />

      <Modal
        opened={editModalOpened}
        onClose={handleCloseEditModal}
        title="Edit Registration"
        size="xl"
        padding="lg"
      >
        {selectedRegistration && (
          <EditRegistrationForm
            registrationId={selectedRegistration}
            onClose={handleCloseEditModal}
          />
        )}
      </Modal>
    </>
  );
}

const RegistrationTicketsTable = ({ registrationId }) => {
  const { data, isPending } = useGetRegistration({ registrationId });

  const [
    editTicketsModalOpened,
    { open: openEditTicketsModal, close: closeEditTicketsModal },
  ] = useDisclosure(false);

  if (isPending) {
    return <Text>Loading...</Text>;
  }

  const canEdit = data?.status === "pending" || data?.status === "invoiced";
  const isCompany = data?.type === "company";

  const ticketCustomFields =
    data?.customFields?.filter((f) => f.appliedTo === "ticket") || [];

  const registrationCustomFields =
    data?.customFields?.filter((f) => f.appliedTo === "registrations") || [];

  return (
    <>
      <Flex direction="column" py="sm" px="md">
        <Text fw={700} size="md" mb="sm">
          Registration details
        </Text>
        {isCompany && (
          <Text mb="sm">VAT number: {data?.vatNumber || "Not provided"}</Text>
        )}
        <Text mb="sm">First name: {data?.firstName || "Not provided"}</Text>
        <Text mb="sm">Last name: {data?.lastName || "Not provided"}</Text>

        {data?.additionalFields && (
          <>
            <Text fw={700} size="md">
              Additional information
            </Text>
            <Flex direction="row" gap="md" wrap="wrap" pb="sm">
              {Object.entries(data?.additionalFields).map(([key, value]) => {
                const field = registrationCustomFields.find(
                  (f) => f.key === key
                );
                return (
                  <AdditionalFieldDisplay
                    key={key}
                    fieldKey={field?.label || key}
                    value={value}
                    fieldType={field?.type}
                  />
                );
              })}
            </Flex>
          </>
        )}

        <Group mb="sm">
          <Text fw={700} size="md">
            Tickets
          </Text>
          {canEdit && (
            <ActionIcon
              variant="light"
              color="blue"
              onClick={openEditTicketsModal}
            >
              <IconEdit size={18} />
            </ActionIcon>
          )}
        </Group>

        <Flex direction="row" gap="md" wrap="wrap">
          {data?.tickets.map((ticket) => (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              customFields={ticketCustomFields}
            />
          ))}
        </Flex>
      </Flex>

      <Modal
        opened={editTicketsModalOpened}
        onClose={closeEditTicketsModal}
        title="Edit Tickets"
        size="xl"
        padding="lg"
      >
        <EditTicketsForm
          registrationId={registrationId}
          onClose={closeEditTicketsModal}
          registrationStatus={data?.status}
        />
      </Modal>
    </>
  );
};

function FilterSearch({ query, setQuery, columnName }) {
  const camelToReadableColumnName = columnName
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase();
  return (
    <TextInput
      label="Tickets"
      description={`Show tickets whose ${camelToReadableColumnName} includes the specified text`}
      placeholder="Search tickets..."
      leftSection={<IconSearch size={16} />}
      rightSection={
        <ActionIcon
          size="sm"
          variant="transparent"
          c="dimmed"
          onClick={() => setQuery(null)}
        >
          <IconX size={14} />
        </ActionIcon>
      }
      value={query?.[columnName] || ""}
      onChange={(e) => {
        if (e.currentTarget.value === "") {
          setQuery(null);
          return;
        }
        setQuery({ [columnName]: e.currentTarget.value });
      }}
    />
  );
}

function StatusFilter({ query, setQuery }) {
  return (
    <Select
      label="Status"
      description="Filter tickets by status"
      placeholder="Filter by status"
      data={[
        { value: "pending", label: "Pending" },
        { value: "approved", label: "Approved" },
        { value: "rejected", label: "Rejected" },
        { value: "invoiced", label: "Invoiced" },
      ]}
      value={query?.status}
      onChange={(value) => {
        setQuery({ status: value });
      }}
      onClear={() => setQuery(null)}
      clearable
    />
  );
}

export default Table;
